// Receipt actions from the drawer (prototype 30-receipts.js A.voidReceipt, A.resend; spec 08 R-22, R-23; spec 06 R-37, R-56):
// void with a reason the customer sees (void_receipt; the VOID copy and the void email are made server side), and
// email the receipt again to the customer or to other addresses (log_share 'emailed' / 'sent_to').
import { useState } from 'react';
import { DbError, rpc } from '../lib/supabase';
import { plain } from '../lib/errors';
import { L } from '../lib/prefs';
import { Btn, Field, Ic, Radios } from '../ui/kit';
import { closeModal, openModal } from '../ui/modal';
import { toast } from '../ui/toast';

export type VoidOptions = { status: string; blocked: string | null; can_void: boolean; can_request: boolean };

/** Why a receipt can't be voided, in words (app.void_block_reason; prototype voidBlock). */
export const blockText = (b: string | null) =>
  b === 'cheque_at_bank' ? L('A cheque on this receipt is already at the bank.', 'شيك في هذا السند في البنك بالفعل.')
    : b === 'cheque_replaced' ? L('A cheque on this receipt was replaced by another cheque.', 'استُبدل شيك في هذا السند بشيك آخر.')
      : null;

const REASON_MAX = 140; // what the customer reads on the verify page and in the email (#127, the phone's void box)

function why(e: unknown) {
  const c = e instanceof DbError ? e.code : '';
  if (c === 'void_blocked') return L('This receipt can\'t be voided now: its money has moved on. Reload to see why.', 'لا يمكن إلغاء هذا السند الآن: انتقلت أمواله. أعد التحميل لمعرفة السبب.');
  if (c === 'already_voided') return L('This receipt was already voided. Reload the page.', 'أُلغي هذا السند بالفعل. أعد تحميل الصفحة.');
  if (c === 'receipt_not_found' || c === 'not_found') return L('This receipt was already voided or isn\'t visible to you. Reload the page.', 'هذا السند أُلغي بالفعل أو غير متاح لك. أعد تحميل الصفحة.');
  if (c === 'void_reason_required') return L('Write a reason.', 'اكتب السبب.');
  if (c === 'share_locked') return L('Your company\'s rules don\'t allow sending receipts to other addresses.', 'قواعد شركتك لا تسمح بإرسال السندات إلى عناوين أخرى.');
  if (c === 'email_invalid') return L('Check the email addresses.', 'تحقق من عناوين البريد.');
  if (c === 'too_many_emails') return L('Up to 10 addresses at a time.', 'حتى 10 عناوين في المرة.');
  if (c === 'email_rate_limited') return (e as DbError).detail === 'addresses'
    ? L('You\'ve sent receipts to many new addresses today. Try again tomorrow.', 'أرسلت سندات إلى عناوين جديدة كثيرة اليوم. حاول غداً.')
    : L('You\'ve sent this receipt several times. Try again in a few minutes.', 'أرسلت هذا السند عدة مرات. حاول بعد دقائق.');
  return plain(e);
}

function VoidBox({ id, number, onDone }: { id: string; number: string; onDone: () => void }) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      await rpc('void_receipt', { p_receipt: id, p_reason: reason.trim() });
      closeModal(); onDone();
      toast(L(`Receipt ${number} voided. The customer is emailed the VOID copy.`, `أُلغي السند ${number}. يُرسل للعميل النسخة الملغاة.`), { icon: 'ban' });
    } catch (e) { toast(why(e), { tone: 'bad' }); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="m-body">
        <p className="lead">{L('This can\'t be reversed after you close the message. The customer is emailed, the QR code shows VOID and the number stays used, so the series has no gaps.', 'يصل العميل بريد بالإلغاء ويظهر رمز QR ملغى ويبقى الرقم مستخدماً حتى لا توجد فجوات.')}</p>
        <Field label={L('Reason', 'السبب')} htmlFor="void-reason" req hint={L('The customer sees this reason on the verify page and in the email. Plain words, up to 140 characters.', 'يرى العميل هذا السبب في صفحة التحقق وفي البريد. كلمات بسيطة، حتى 140 حرفاً.')}>
          <textarea id="void-reason" className="inp" rows={3} maxLength={REASON_MAX} value={reason} onChange={e => setReason(e.target.value)} data-autofocus />
        </Field>
        <p className="muted sm count" aria-live="polite"><bdi dir="ltr">{reason.length} / {REASON_MAX}</bdi></p>
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="danger" busy={busy} disabled={!reason.trim()} onClick={() => void go()}>{L('Void receipt', 'إلغاء السند')}</Btn>
      </footer>
    </>
  );
}

export const openVoid = (id: string, number: string, onDone: () => void) =>
  openModal({ title: L(`Void receipt ${number}?`, `إلغاء السند ${number}؟`), size: 'sm', bare: true, body: <VoidBox id={id} number={number} onDone={onDone} /> });

const splitEmails = (s: string) => s.split(/[\s,;]+/).map(x => x.trim().toLowerCase()).filter(Boolean);
const okEmail = (x: string) => /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(x);

function EmailBox({ id, customer, cc, voided, onDone }: { id: string; customer: string | null; cc: string[]; voided: boolean; onDone: () => void }) {
  const [to, setTo] = useState<'customer' | 'other'>(customer ? 'customer' : 'other');
  const [other, setOther] = useState('');
  const [alsoCustomer, setAlsoCustomer] = useState(false);
  const [busy, setBusy] = useState(false);
  const list = splitEmails(other);
  const bad = list.filter(x => !okEmail(x));
  const ready = to === 'customer' ? !!customer : list.length > 0 && !bad.length && list.length <= 10;
  const go = async () => {
    setBusy(true);
    try {
      if (to === 'customer') await rpc('log_share', { p_receipt: id, p_kind: 'emailed', p_detail: {} });
      else await rpc('log_share', { p_receipt: id, p_kind: 'sent_to', p_detail: { emails: list, copy_customer: alsoCustomer && !!customer } });
      closeModal(); onDone();
      const who = to === 'customer' ? customer! : list.join(', ');
      toast(L('Receipt emailed to ' + who, 'أُرسل السند إلى ' + who), { icon: 'mail' });
    } catch (e) { toast(why(e), { tone: 'bad' }); }
    finally { setBusy(false); }
  };
  return (
    <>
      <div className="m-body">
        <Radios<'customer' | 'other'> name="em-to" value={to} onChange={setTo} opts={[
          ['customer', L('The customer', 'العميل'), customer ? customer + (cc.length ? ' · ' + L('copy to ', 'نسخة إلى ') + cc.join(', ') : '') : L('No email on file', 'لا يوجد بريد مسجل')],
          ['other', L('Other addresses', 'عناوين أخرى'), L('Up to 10, separated by commas.', 'حتى 10، تفصل بينها فواصل.')],
        ]} />
        {to === 'other' ? <>
          <Field label={L('Email addresses', 'عناوين البريد')} htmlFor="em-other" err={bad.length ? L('Check: ', 'تحقق: ') + bad.join(', ') : list.length > 10 ? L('Up to 10 addresses at a time.', 'حتى 10 عناوين في المرة.') : undefined}>
            <input id="em-other" className="inp" type="text" inputMode="email" autoComplete="off" dir="ltr" value={other} onChange={e => setOther(e.target.value)} placeholder="accounts@example.com" data-autofocus />
          </Field>
          {customer ? <label className="chk"><input type="checkbox" checked={alsoCustomer} onChange={e => setAlsoCustomer(e.target.checked)} /><span className="box"><Ic n="check" /></span>{L('Also send to the customer', 'أرسل للعميل أيضاً')}</label> : null}
        </> : null}
        <p className="muted sm">{voided ? L('This is the VOID copy the customer was emailed. The original stays in the archive.', 'هذه النسخة الملغاة التي أُرسلت للعميل. يبقى الأصل في الأرشيف.') : L('The PDF is attached, as issued.', 'الملف مرفق كما صدر.')}</p>
      </div>
      <footer className="m-foot">
        <Btn kind="ghost" onClick={closeModal}>{L('Cancel', 'إلغاء')}</Btn>
        <Btn kind="primary" icon="mail" busy={busy} disabled={!ready} onClick={() => void go()}>{L('Send', 'إرسال')}</Btn>
      </footer>
    </>
  );
}

export const openEmail = (o: { id: string; number: string; customer: string | null; cc: string[]; voided: boolean; onDone: () => void }) =>
  openModal({ title: L('Email the customer again', 'إرسال للعميل مجدداً'), sub: L('Receipt ', 'سند ') + o.number, size: 'sm', bare: true, body: <EmailBox {...o} /> });
